import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  Label,
  Badge,
  Spinner,
} from "reactstrap";

import CountUp from "react-countup";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import api from "../../../utills/api";

const RATE_PLAN = "BAR";

const RatesManagement = () => {
  document.title = "HMS | Rates Management";

  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [roomTypes, setRoomTypes] = useState([]);
  const [rateData, setRateData] = useState([]);
  const [rateLogs, setRateLogs] = useState([]);

  const [filters, setFilters] = useState({
    roomType: "",
    startDate: "",
    endDate: "",
  });

  const [bulkRate, setBulkRate] = useState({
    roomTypeId: "",
    startDate: "",
    endDate: "",
    price: "",
  });

  const [widgets, setWidgets] = useState([
    {
      label: "Average Rate",
      value: 0,
      prefix: "₹",
      icon: "bx bx-money",
      color: "#0d6efd",
      bgColor: "rgba(13,110,253,.12)",
    },
    {
      label: "Highest Rate",
      value: 0,
      prefix: "₹",
      icon: "bx bx-trending-up",
      color: "#198754",
      bgColor: "rgba(25,135,84,.12)",
    },
    {
      label: "Lowest Rate",
      value: 0,
      prefix: "₹",
      icon: "bx bx-trending-down",
      color: "#ffc107",
      bgColor: "rgba(255,193,7,.15)",
    },
    {
      label: "Rate Records",
      value: 0,
      icon: "bx bx-refresh",
      color: "#dc3545",
      bgColor: "rgba(220,53,69,.12)",
    },
  ]);

  /* =========================================================
     HELPERS
  ========================================================= */

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(
      date.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
      date.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const parseLocalDate = (dateString) => {
    if (!dateString) return null;

    const [year, month, day] =
      dateString.split("-").map(Number);

    return new Date(
      year,
      month - 1,
      day
    );
  };

  const formatDisplayDate = (dateString) => {
    const date =
      parseLocalDate(dateString);

    if (!date) return "-";

    return date.toLocaleDateString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
      }
    );
  };

  const formatDayName = (dateString) => {
    const date =
      parseLocalDate(dateString);

    if (!date) return "";

    return date.toLocaleDateString(
      "en-IN",
      {
        weekday: "short",
      }
    );
  };

  const formatCurrency = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "-";
    }

    return `₹${Number(value).toLocaleString(
      "en-IN"
    )}`;
  };

  const getDateRange = (
    startDate,
    endDate
  ) => {
    if (!startDate || !endDate) {
      return [];
    }

    const dates = [];

    const current =
      parseLocalDate(startDate);

    const end =
      parseLocalDate(endDate);

    if (!current || !end) {
      return [];
    }

    while (current <= end) {
      dates.push(formatDate(current));

      current.setDate(
        current.getDate() + 1
      );
    }

    return dates;
  };

  /* =========================================================
     LOAD ROOM TYPES
  ========================================================= */

  const loadRoomTypes = useCallback(
    async () => {
      try {
        const res = await api.get(
          "/manage/room-types"
        );

        setRoomTypes(
          Array.isArray(res.data)
            ? res.data
            : []
        );
      } catch (err) {
        console.error(
          "ROOM TYPES ERROR:",
          err
        );
      }
    },
    []
  );

  /* =========================================================
     LOAD RATE LOGS
  ========================================================= */

  const loadLogs = useCallback(
    async () => {
      try {
        const res = await api.get(
          "/rates/logs"
        );

        const logs = (
          res.data?.logs || []
        ).map((item) => ({
          date: item.date || "-",

          roomType:
            item.roomTypeName ||
            "-",

          oldRate:
            Number(item.oldRate || 0),

          newRate:
            Number(item.newRate || 0),

          updatedBy:
            item.updatedBy ||
            "Admin",
        }));

        setRateLogs(logs);
      } catch (err) {
        console.error(
          "RATE LOG ERROR:",
          err
        );
      }
    },
    []
  );

  /* =========================================================
     LOAD RATE CALENDAR
  ========================================================= */

  const loadCalendar = useCallback(
    async () => {
      if (
        !filters.startDate ||
        !filters.endDate
      ) {
        return;
      }

      try {
        setLoading(true);

        const res =
          await api.get(
            "/rates/calendar",
            {
              params: {
                roomTypeId:
                  filters.roomType || "",

                startDate:
                  filters.startDate,

                endDate:
                  filters.endDate,

                ratePlanName:
                  RATE_PLAN,
              },
            }
          );

        const calendar =
          res.data?.data || [];

        const dates = getDateRange(
          filters.startDate,
          filters.endDate
        );

        let highest = 0;
        let lowest = 0;
        let total = 0;
        let count = 0;

        const rows = calendar.map(
          (roomType) => {
            const row = {
              roomType:
                roomType.roomTypeName ||
                "-",
            };

            /*
             * Create an empty column
             * for every requested date.
             */
            dates.forEach(
              (date) => {
                row[date] = null;
              }
            );

            /*
             * IMPORTANT:
             * Map the rate using the actual
             * database date instead of array index.
             */
            (
              roomType.rates || []
            ).forEach((rate) => {
              if (!rate?.date) {
                return;
              }

              const rateDate =
                formatDate(
                  new Date(rate.date)
                );

              /*
               * Only put the rate into
               * the matching calendar date.
               */
              if (
                Object.prototype.hasOwnProperty.call(
                  row,
                  rateDate
                )
              ) {
                const price =
                  Number(
                    rate.price || 0
                  );

                row[rateDate] =
                  price;

                total += price;
                count++;

                if (
                  price > highest
                ) {
                  highest =
                    price;
                }

                if (
                  lowest === 0 ||
                  price < lowest
                ) {
                  lowest =
                    price;
                }
              }
            });

            return row;
          }
        );

        const average =
          count > 0
            ? Math.round(
                total / count
              )
            : 0;

        setRateData(rows);

        setWidgets([
          {
            label: "Average Rate",
            value: average,
            prefix: "₹",
            icon: "bx bx-money",
            color: "#0d6efd",
            bgColor:
              "rgba(13,110,253,.12)",
          },
          {
            label: "Highest Rate",
            value: highest,
            prefix: "₹",
            icon:
              "bx bx-trending-up",
            color: "#198754",
            bgColor:
              "rgba(25,135,84,.12)",
          },
          {
            label: "Lowest Rate",
            value: lowest,
            prefix: "₹",
            icon:
              "bx bx-trending-down",
            color: "#ffc107",
            bgColor:
              "rgba(255,193,7,.15)",
          },
          {
            label: "Rate Records",
            value: count,
            icon: "bx bx-refresh",
            color: "#dc3545",
            bgColor:
              "rgba(220,53,69,.12)",
          },
        ]);
      } catch (err) {
        console.error(
          "RATE CALENDAR ERROR:",
          err
        );

        setRateData([]);
      } finally {
        setLoading(false);
      }
    },
    [
      filters.startDate,
      filters.endDate,
      filters.roomType,
    ]
  );

  /* =========================================================
     UPDATE RATES
  ========================================================= */

  const handleRateUpdate =
    async () => {
      const {
        roomTypeId,
        startDate,
        endDate,
        price,
      } = bulkRate;

      if (!roomTypeId) {
        alert(
          "Please select a room type."
        );
        return;
      }

      if (!startDate) {
        alert(
          "Please select a start date."
        );
        return;
      }

      if (!endDate) {
        alert(
          "Please select an end date."
        );
        return;
      }

      if (
        parseLocalDate(endDate) <
        parseLocalDate(startDate)
      ) {
        alert(
          "End date cannot be before start date."
        );
        return;
      }

      if (
        price === "" ||
        Number(price) < 0
      ) {
        alert(
          "Please enter a valid rate."
        );
        return;
      }

      try {
        setUpdating(true);

        await api.post(
          "/rates",
          {
            roomTypeId,

            ratePlanName:
              RATE_PLAN,

            startDate,

            endDate,

            price: Number(price),
          }
        );

        alert(
          "Rates updated successfully."
        );

        /*
         * Refresh both calendar and logs.
         */
        await Promise.all([
          loadCalendar(),
          loadLogs(),
        ]);

        /*
         * Keep dates but clear
         * room type and price.
         */
        setBulkRate({
          ...bulkRate,
          roomTypeId: "",
          price: "",
        });
      } catch (err) {
        console.error(
          "RATE UPDATE ERROR:",
          err
        );

        alert(
          err.response?.data
            ?.message ||
            "Failed to update rates."
        );
      } finally {
        setUpdating(false);
      }
    };

  /* =========================================================
     INITIAL LOAD
  ========================================================= */

  useEffect(() => {
    const today =
      new Date();

    const start =
      formatDate(today);

    const end =
      new Date(today);

    end.setDate(
      end.getDate() + 6
    );

    const endDate =
      formatDate(end);

    setFilters({
      roomType: "",
      startDate: start,
      endDate: endDate,
    });

    setBulkRate({
      roomTypeId: "",
      startDate: start,
      endDate: endDate,
      price: "",
    });

    loadRoomTypes();
  }, [loadRoomTypes]);

  /* =========================================================
     LOAD CALENDAR WHEN FILTER DATES CHANGE
  ========================================================= */

  useEffect(() => {
    if (
      filters.startDate &&
      filters.endDate
    ) {
      loadCalendar();
      loadLogs();
    }
  }, [
    filters.startDate,
    filters.endDate,
    loadCalendar,
    loadLogs,
  ]);

  /* =========================================================
     CALENDAR DATES
  ========================================================= */

  const calendarDates = useMemo(
    () =>
      getDateRange(
        filters.startDate,
        filters.endDate
      ),
    [
      filters.startDate,
      filters.endDate,
    ]
  );

  /* =========================================================
     RATE TABLE COLUMNS
  ========================================================= */

  const rateColumns =
    useMemo(() => {
      const columns = [
        {
          header: "Room Type",
          accessorKey:
            "roomType",
          cell: ({
            getValue,
          }) => (
            <span className="fw-semibold">
              {getValue() || "-"}
            </span>
          ),
        },
      ];

      calendarDates.forEach(
        (date) => {
          columns.push({
            header: (
              <div className="text-center">
                <div className="fw-semibold">
                  {formatDisplayDate(
                    date
                  )}
                </div>

                <small className="text-muted">
                  {formatDayName(
                    date
                  )}
                </small>
              </div>
            ),

            accessorKey: date,

            cell: ({
              getValue,
            }) => {
              const value =
                getValue();

              if (
                value === null ||
                value ===
                  undefined ||
                value === ""
              ) {
                return (
                  <span className="text-muted">
                    —
                  </span>
                );
              }

              return (
                <span className="fw-semibold">
                  {formatCurrency(
                    value
                  )}
                </span>
              );
            },
          });
        }
      );

      return columns;
    }, [calendarDates]);

  /* =========================================================
     LOG COLUMNS
  ========================================================= */

  const logColumns =
    useMemo(
      () => [
        {
          header: "Date",
          accessorKey: "date",

          cell: ({
            getValue,
          }) => {
            const value =
              getValue();

            if (!value) {
              return "-";
            }

            return formatDisplayDate(
              value
            );
          },
        },

        {
          header: "Room Type",
          accessorKey:
            "roomType",
        },

        {
          header: "Old Rate",
          accessorKey:
            "oldRate",

          cell: ({
            getValue,
          }) =>
            formatCurrency(
              getValue()
            ),
        },

        {
          header: "New Rate",
          accessorKey:
            "newRate",

          cell: ({
            getValue,
          }) => (
            <span className="fw-semibold text-success">
              {formatCurrency(
                getValue()
              )}
            </span>
          ),
        },

        {
          header: "Updated By",
          accessorKey:
            "updatedBy",
        },
      ],
      []
    );

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="page-content">
      <Container fluid>

        <BreadCrumb
          title="Channel Manager"
          pageTitle="Rates Management"
        />

        {/* =================================================
            WIDGETS
        ================================================= */}

        <Row>
          {widgets.map(
            (item, index) => (
              <Col
                xl={3}
                md={6}
                key={index}
              >
                <Card>
                  <CardBody className="d-flex justify-content-between align-items-center">

                    <div>
                      <p className="text-uppercase text-muted fw-semibold mb-1">
                        {item.label}
                      </p>

                      <h3 className="mb-0">
                        {item.prefix ||
                          ""}

                        <CountUp
                          start={0}
                          end={
                            item.value
                          }
                          duration={
                            1.2
                          }
                          separator=","
                        />
                      </h3>
                    </div>

                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        width:
                          "50px",
                        height:
                          "50px",
                        borderRadius:
                          "10px",
                        backgroundColor:
                          item.bgColor,
                      }}
                    >
                      <i
                        className={
                          item.icon
                        }
                        style={{
                          fontSize:
                            "24px",
                          color:
                            item.color,
                        }}
                      />
                    </div>

                  </CardBody>
                </Card>
              </Col>
            )
          )}
        </Row>

        {/* =================================================
            FILTERS
        ================================================= */}

        <Row>
          <Col lg={12}>
            <Card>

              <CardHeader>
                <h4 className="card-title mb-0">
                  Rate Filters
                </h4>
              </CardHeader>

              <CardBody>
                <Row>

                  <Col md={3}>
                    <Label>
                      Start Date
                    </Label>

                    <Input
                      type="date"
                      value={
                        filters.startDate
                      }
                      onChange={(
                        e
                      ) =>
                        setFilters({
                          ...filters,
                          startDate:
                            e.target
                              .value,
                        })
                      }
                    />
                  </Col>

                  <Col md={3}>
                    <Label>
                      End Date
                    </Label>

                    <Input
                      type="date"
                      value={
                        filters.endDate
                      }
                      min={
                        filters.startDate ||
                        undefined
                      }
                      onChange={(
                        e
                      ) =>
                        setFilters({
                          ...filters,
                          endDate:
                            e.target
                              .value,
                        })
                      }
                    />
                  </Col>

                  <Col md={3}>
                    <Label>
                      Room Type
                    </Label>

                    <Input
                      type="select"
                      value={
                        filters.roomType
                      }
                      onChange={(
                        e
                      ) =>
                        setFilters({
                          ...filters,
                          roomType:
                            e.target
                              .value,
                        })
                      }
                    >
                      <option value="">
                        All Room Types
                      </option>

                      {roomTypes.map(
                        (
                          room
                        ) => (
                          <option
                            key={
                              room._id
                            }
                            value={
                              room._id
                            }
                          >
                            {
                              room.name
                            }
                          </option>
                        )
                      )}
                    </Input>
                  </Col>

                  <Col
                    md={3}
                    className="d-flex align-items-end"
                  >
                    <Button
                      color="primary"
                      className="w-100"
                      onClick={
                        loadCalendar
                      }
                      disabled={
                        loading
                      }
                    >
                      {loading ? (
                        <>
                          <Spinner
                            size="sm"
                            className="me-2"
                          />
                          Loading...
                        </>
                      ) : (
                        <>
                          <i className="bx bx-filter-alt me-1" />
                          Apply Filters
                        </>
                      )}
                    </Button>
                  </Col>

                </Row>
              </CardBody>

            </Card>
          </Col>
        </Row>

        {/* =================================================
            RATE CALENDAR
        ================================================= */}

        <Row>
          <Col lg={12}>
            <Card>

              <CardHeader className="d-flex justify-content-between align-items-center">

                <div>
                  <h4 className="card-title mb-1">
                    Rate Calendar View
                  </h4>

                  <small className="text-muted">
                    Rate Plan:{" "}
                    <strong>
                      {RATE_PLAN}
                    </strong>
                  </small>
                </div>

                <Badge color="success">
                  <i className="bx bx-radio-circle-marked me-1" />
                  Live Rates
                </Badge>

              </CardHeader>

              <CardBody>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner
                      color="primary"
                    />

                    <div className="text-muted mt-2">
                      Loading rates...
                    </div>
                  </div>
                ) : rateData.length ===
                  0 ? (
                  <div className="text-center py-5">
                    <i
                      className="bx bx-calendar-x"
                      style={{
                        fontSize:
                          "42px",
                      }}
                    />

                    <h5 className="mt-3">
                      No rates found
                    </h5>

                    <p className="text-muted mb-0">
                      No BAR rates are
                      available for the
                      selected dates.
                    </p>
                  </div>
                ) : (
                  <TableContainer
                    columns={
                      rateColumns
                    }
                    data={
                      rateData
                    }
                    customPageSize={
                      10
                    }
                    isPagination={
                      false
                    }
                    className="text-nowrap"
                    theadClass="table-light"
                  />
                )}

              </CardBody>

            </Card>
          </Col>
        </Row>

        {/* =================================================
            BOTTOM SECTION
        ================================================= */}

        <Row>

          {/* =================================================
              BULK UPDATE
          ================================================= */}

          <Col lg={4}>

            <Card
              style={{
                minHeight:
                  "430px",
              }}
            >

              <CardHeader>
                <div>
                  <h4 className="card-title mb-1">
                    Bulk Rate Update
                  </h4>

                  <small className="text-muted">
                    Update BAR rate for
                    multiple dates
                  </small>
                </div>
              </CardHeader>

              <CardBody>

                {/* ROOM TYPE */}

                <div className="mb-3">
                  <Label>
                    Room Type{" "}
                    <span className="text-danger">
                      *
                    </span>
                  </Label>

                  <Input
                    type="select"
                    value={
                      bulkRate.roomTypeId
                    }
                    onChange={(
                      e
                    ) =>
                      setBulkRate({
                        ...bulkRate,
                        roomTypeId:
                          e.target
                            .value,
                      })
                    }
                  >
                    <option value="">
                      Select Room Type
                    </option>

                    {roomTypes.map(
                      (
                        room
                      ) => (
                        <option
                          key={
                            room._id
                          }
                          value={
                            room._id
                          }
                        >
                          {
                            room.name
                          }
                        </option>
                      )
                    )}
                  </Input>
                </div>

                {/* START DATE */}

                <div className="mb-3">
                  <Label>
                    Start Date{" "}
                    <span className="text-danger">
                      *
                    </span>
                  </Label>

                  <Input
                    type="date"
                    value={
                      bulkRate.startDate
                    }
                    onChange={(
                      e
                    ) =>
                      setBulkRate({
                        ...bulkRate,
                        startDate:
                          e.target
                            .value,
                      })
                    }
                  />
                </div>

                {/* END DATE */}

                <div className="mb-3">
                  <Label>
                    End Date{" "}
                    <span className="text-danger">
                      *
                    </span>
                  </Label>

                  <Input
                    type="date"
                    value={
                      bulkRate.endDate
                    }
                    min={
                      bulkRate.startDate ||
                      undefined
                    }
                    onChange={(
                      e
                    ) =>
                      setBulkRate({
                        ...bulkRate,
                        endDate:
                          e.target
                            .value,
                      })
                    }
                  />
                </div>

                {/* RATE */}

                <div className="mb-4">
                  <Label>
                    Rate{" "}
                    <span className="text-danger">
                      *
                    </span>
                  </Label>

                  <div className="input-group">
                    <span className="input-group-text">
                      ₹
                    </span>

                    <Input
                      type="number"
                      min="0"
                      step="1"
                      placeholder="Enter Rate"
                      value={
                        bulkRate.price
                      }
                      onChange={(
                        e
                      ) =>
                        setBulkRate({
                          ...bulkRate,
                          price:
                            e.target
                              .value,
                        })
                      }
                    />
                  </div>
                </div>

                <Button
                  color="success"
                  className="w-100"
                  onClick={
                    handleRateUpdate
                  }
                  disabled={
                    updating
                  }
                >
                  {updating ? (
                    <>
                      <Spinner
                        size="sm"
                        className="me-2"
                      />
                      Updating Rates...
                    </>
                  ) : (
                    <>
                      <i className="bx bx-save me-1" />
                      Update Rates
                    </>
                  )}
                </Button>

              </CardBody>

            </Card>

          </Col>

          {/* =================================================
              LOGS
          ================================================= */}

          <Col lg={8}>

            <Card
              style={{
                minHeight:
                  "430px",
              }}
            >

              <CardHeader className="d-flex justify-content-between align-items-center">

                <div>
                  <h4 className="card-title mb-1">
                    Recent Rate Updates
                  </h4>

                  <small className="text-muted">
                    Latest BAR rate changes
                  </small>
                </div>

                <Badge color="light">
                  {rateLogs.length}{" "}
                  Records
                </Badge>

              </CardHeader>

              <CardBody>

                {rateLogs.length ===
                0 ? (
                  <div className="text-center py-5">
                    <i
                      className="bx bx-history"
                      style={{
                        fontSize:
                          "40px",
                      }}
                    />

                    <h5 className="mt-3">
                      No rate updates
                    </h5>

                    <p className="text-muted mb-0">
                      Rate update history
                      will appear here.
                    </p>
                  </div>
                ) : (
                  <TableContainer
                    columns={
                      logColumns
                    }
                    data={
                      rateLogs
                    }
                    customPageSize={
                      5
                    }
                    isPagination={
                      false
                    }
                    className="text-nowrap"
                    theadClass="table-light"
                  />
                )}

              </CardBody>

            </Card>

          </Col>

        </Row>

      </Container>
    </div>
  );
};

export default RatesManagement;